/**
 * @file pwa/task-pack-manifest.ts
 * @description 稽核任務離線包 manifest / resource 操作。
 */
import {
  getOfflineDb,
  type CachedTaskPack,
  type CachedTaskResource,
  type OutboxEntry,
} from '@/pwa/db';

export type TaskPackDeleteResult =
  | { ok: true; blocked: false }
  | { ok: false; blocked: true; pendingOutbox: number; pendingMedia: number };

export async function putTaskPack(pack: CachedTaskPack): Promise<void> {
  const db = await getOfflineDb();
  await db.put('cache_tasks', pack);
}

export async function getTaskPack(taskId: number): Promise<CachedTaskPack | null> {
  const db = await getOfflineDb();
  return (await db.get('cache_tasks', taskId)) ?? null;
}

export async function listTaskPacks(): Promise<CachedTaskPack[]> {
  const db = await getOfflineDb();
  const rows = await db.getAll('cache_tasks');
  return rows.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function putTaskResource(resource: CachedTaskResource): Promise<void> {
  const db = await getOfflineDb();
  await db.put('cache_task_resources', resource);
}

export async function listTaskResources(taskId: number): Promise<CachedTaskResource[]> {
  const db = await getOfflineDb();
  return db.getAllFromIndex('cache_task_resources', 'by-task', taskId);
}

export async function countTaskPendingOutbox(taskId: number): Promise<number> {
  const db = await getOfflineDb();
  const pending = await db.getAllFromIndex('outbox', 'by-status', 'pending');
  return pending.filter((row) => outboxBelongsToTask(row, taskId)).length;
}

export async function countTaskPendingMedia(taskId: number): Promise<number> {
  const db = await getOfflineDb();
  const pending = await db.getAllFromIndex('cache_media', 'by-status', 'pending');
  return pending.filter((row) => row.ownerType === 'audit_task' && row.ownerId === taskId).length;
}

export async function deleteTaskPack(
  taskId: number,
  opts: { force?: boolean } = {},
): Promise<TaskPackDeleteResult> {
  const db = await getOfflineDb();
  const [pendingOutbox, pendingMedia] = await Promise.all([
    countTaskPendingOutbox(taskId),
    countTaskPendingMedia(taskId),
  ]);

  if (!opts.force && (pendingOutbox > 0 || pendingMedia > 0)) {
    return { ok: false, blocked: true, pendingOutbox, pendingMedia };
  }

  const [resources, apiRows, allResources, allApi] = await Promise.all([
    listTaskResources(taskId),
    db.getAllFromIndex('cache_api', 'by-task', taskId),
    db.getAll('cache_task_resources'),
    db.getAll('cache_api'),
  ]);

  // 引用计数：任何文件类资源若被其他 task 引用，cache_media 不能删
  // 否则 pack B 的 manifest 仍说 cached 但底层 blob 被 pack A 删除（PR #2 review #3）
  const FILE_TYPES = new Set<CachedTaskResource['resourceType']>([
    'case-document',
    'form-template-docx',
    'form-attachment',
  ]);
  const mediaResourceIds = new Set(
    resources.filter((r) => FILE_TYPES.has(r.resourceType)).map((r) => r.resourceId),
  );
  const sharedMediaIds = new Set<string>();
  for (const r of allResources) {
    if (r.taskId === taskId) continue;
    if (FILE_TYPES.has(r.resourceType) && mediaResourceIds.has(r.resourceId)) {
      sharedMediaIds.add(r.resourceId);
    }
  }

  // cache_api 以 URL 为主键、taskId 只是最近写入者；保守起见只删确认当前 task
  // 是唯一 owner 的行（其他 task 不再引用该 URL）
  const apiUrlsOfOtherTasks = new Set<string>();
  for (const row of allApi) {
    if (typeof row.taskId === 'number' && row.taskId !== taskId) {
      apiUrlsOfOtherTasks.add(row.url);
    }
  }

  const tx = db.transaction(
    ['cache_tasks', 'cache_task_resources', 'cache_api', 'cache_media'],
    'readwrite',
  );
  await tx.objectStore('cache_tasks').delete(taskId);

  for (const resource of resources) {
    await tx.objectStore('cache_task_resources').delete(resource.id);
    if (FILE_TYPES.has(resource.resourceType) && !sharedMediaIds.has(resource.resourceId)) {
      await tx.objectStore('cache_media').delete(`file:${resource.resourceId}`);
    }
  }

  for (const row of apiRows) {
    if (!apiUrlsOfOtherTasks.has(row.url)) {
      await tx.objectStore('cache_api').delete(row.url);
    }
  }

  await tx.done;
  return { ok: true, blocked: false };
}

export function makeTaskResourceId(
  taskId: number,
  resourceType: CachedTaskResource['resourceType'],
  resourceId: string | number,
): string {
  return `${taskId}:${resourceType}:${resourceId}`;
}

function outboxBelongsToTask(row: OutboxEntry, taskId: number): boolean {
  const body = row.body as { taskId?: unknown } | undefined;
  const fromBody = typeof body?.taskId === 'number' && body.taskId === taskId;
  const fromLoose = (row as OutboxEntry & { taskId?: number }).taskId === taskId;
  return fromBody || fromLoose;
}
